import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { tareasApi, entidadesApi, usuariosApi, contactosApi, exportApi } from '../services/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Plus, CheckSquare, Square, Clock, Calendar as CalendarIcon, ChevronLeft, ChevronRight,
  List, Trash2, Pencil, AlertTriangle, Download, LayoutGrid, Zap, Star, Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../components/ui/ConfirmDeleteDialog';
import { EmptyState } from '../components/ui/EmptyState';
import { formatFecha } from '../utils/dateFormat';

const ESTADO_STYLE = {
  pendiente: 'bg-amber-50 border-amber-200 text-amber-700',
  en_curso:  'bg-blue-50 border-blue-200 text-blue-700',
  hecha:     'bg-emerald-50 border-emerald-200 text-emerald-700',
  cancelada: 'bg-ink-100 border-ink-200 text-ink-500',
};
const ESTADO_LABEL = { pendiente: 'Pendiente', en_curso: 'En curso', hecha: 'Hecha', cancelada: 'Cancelada' };
const PRIORIDAD_STYLE = {
  alta:  'bg-red-50 border-red-200 text-red-700',
  media: 'bg-amber-50 border-amber-200 text-amber-700',
  baja:  'bg-ink-100 border-ink-200 text-ink-500',
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtDateLocal = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

const emptyForm = (myId) => ({
  titulo: '', descripcion: '',
  vencimiento: todayIso(),
  asignado_user_id: myId || '',
  prioridad: 'media',
  urgente: false, importante: false,
  entidad_id: '', contacto_id: '',
});

const Tareas = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [tareas, setTareas] = useState([]);
  const [entidades, setEntidades] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [contactos, setContactos] = useState([]);
  const [vista, setVista] = useState('lista'); // lista | calendario
  const [filtroScope, setFiltroScope] = useState(() => isAdmin ? 'todas' : 'mias');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroPrioridad, setFiltroPrioridad] = useState('');
  const [filtroRol, setFiltroRol] = useState('');
  const [filtroResponsable, setFiltroResponsable] = useState('');
  const [filtroDesde, setFiltroDesde] = useState('');
  const [filtroHasta, setFiltroHasta] = useState('');
  const [mes, setMes] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm(user?.id));
  const [err, setErr] = useState('');
  const [confirmDel, setConfirmDel] = useState({ open: false, item: null });
  // Iter56: mappings de sync para mostrar badges 🔄
  const [syncMap, setSyncMap] = useState({}); // { crm_tarea_id: [providers] }
  const [syncing, setSyncing] = useState(false);

  const cargarSync = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks-sync/mappings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const d = await r.json();
      const m = {};
      for (const it of (d.items || [])) {
        if (!m[it.crm_tarea_id]) m[it.crm_tarea_id] = new Set();
        m[it.crm_tarea_id].add(it.provider);
      }
      const out = {};
      for (const k of Object.keys(m)) out[k] = Array.from(m[k]);
      setSyncMap(out);
    } catch (_) { /* silent */ }
  };

  const sincronizarTareasAhora = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks-sync/sync-now`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      const c = d.pull?.creados_crm || 0;
      const u = (d.push?.creados?.length || 0) + (d.push?.actualizados?.length || 0);
      toast.success(`Sync tareas · ${c} importadas · ${u} exportadas`);
      cargar();
      cargarSync();
    } catch (e) { toast.error(`Sync: ${e.message}`); }
    finally { setSyncing(false); }
  };

  const cargar = useCallback(async () => {
    try {
      const params = {};
      // mias solo cuando no hay un responsable específico seleccionado
      if (filtroScope === 'mias' && !filtroResponsable && !filtroRol) params.mias = true;
      if (filtroEstado) params.estado = filtroEstado;
      if (filtroPrioridad) params.prioridad = filtroPrioridad;
      if (filtroRol) params.asignado_rol = filtroRol;
      if (filtroResponsable) params.asignado_user_id = filtroResponsable;
      if (filtroDesde) params.desde = filtroDesde;
      if (filtroHasta) params.hasta = filtroHasta;
      const t = await tareasApi.list(params);
      setTareas(t);
    } catch (e) { console.error(e); }
  }, [filtroScope, filtroEstado, filtroPrioridad, filtroRol, filtroResponsable, filtroDesde, filtroHasta]);

  useEffect(() => {
    (async () => {
      const [ents, us, cs] = await Promise.all([
        entidadesApi.list({ limit: 1000 }).catch(() => []),
        isAdmin ? usuariosApi.list({ estado: 'activo' }).catch(() => [{ id: user.id, nombre: user.nombre, apellidos: user.apellidos }]) : Promise.resolve([{ id: user.id, nombre: user.nombre, apellidos: user.apellidos || '' }]),
        contactosApi.list({ limit: 1000 }).catch(() => []),
      ]);
      setEntidades(ents); setUsuarios(us); setContactos(cs);
    })();
    // eslint-disable-next-line
  }, [isAdmin]);

  // Set de IDs de usuarios activos — para bloquear edición de tareas de inactivos
  const activeUserIds = useMemo(() => new Set(usuarios.map((u) => u.id)), [usuarios]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { cargarSync(); }, []);

  const abrirNueva = () => {
    setEditing(null);
    setForm(emptyForm(user?.id));
    setErr(''); setDialogOpen(true);
  };
  const abrirEditar = (t) => {
    setEditing(t);
    setForm({
      titulo: t.titulo,
      descripcion: t.descripcion || '',
      vencimiento: t.vencimiento || '',
      asignado_user_id: t.asignado_user_id,
      prioridad: t.prioridad,
      urgente: !!t.urgente,
      importante: !!t.importante,
      entidad_id: t.entidad_id || '',
      contacto_id: t.contacto_id || '',
    });
    setErr(''); setDialogOpen(true);
  };
  const guardar = async (ev) => {
    ev.preventDefault(); setErr('');
    if (!form.titulo.trim()) { setErr('El título es obligatorio'); return; }
    if (!form.asignado_user_id) { setErr('Selecciona responsable'); return; }
    try {
      const payload = {
        titulo: form.titulo,
        descripcion: form.descripcion || null,
        vencimiento: form.vencimiento || null,
        asignado_user_id: form.asignado_user_id,
        prioridad: form.prioridad,
        urgente: !!form.urgente,
        importante: !!form.importante,
        entidad_id: form.entidad_id || null,
        contacto_id: form.contacto_id || null,
      };
      if (editing) await tareasApi.update(editing.id, payload);
      else await tareasApi.create(payload);
      setDialogOpen(false);
      cargar();
    } catch (e) { setErr(e.message); }
  };
  const toggleEstado = async (t) => {
    const next = t.estado === 'hecha' ? 'pendiente' : 'hecha';
    try { await tareasApi.update(t.id, { estado: next }); cargar(); }
    catch (e) { alert(e.message); }
  };
  const toggleCuadrante = async (t, campo) => {
    try { await tareasApi.update(t.id, { [campo]: !t[campo] }); cargar(); }
    catch (e) { toast.error(e.message); }
  };
  const moverCuadrante = async (tarea, targetQ) => {
    if (!!tarea.urgente === targetQ.urgente && !!tarea.importante === targetQ.importante) return;
    try { await tareasApi.update(tarea.id, { urgente: targetQ.urgente, importante: targetQ.importante }); cargar(); }
    catch (e) { toast.error(e.message); }
  };
  const borrar = (t) => setConfirmDel({ open: true, item: t });
  const confirmarBorrar = async () => {
    if (!confirmDel.item) return;
    await tareasApi.delete(confirmDel.item.id);
    cargar();
  };

  // Calendario mensual
  const calendarDays = useMemo(() => {
    const first = new Date(mes.y, mes.m, 1);
    const last = new Date(mes.y, mes.m + 1, 0);
    const startWeekday = (first.getDay() + 6) % 7; // Lunes = 0
    const days = [];
    for (let i = 0; i < startWeekday; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) days.push(new Date(mes.y, mes.m, d));
    return days;
  }, [mes]);

  const tareasPorDia = useMemo(() => {
    const map = {};
    tareas.forEach((t) => {
      if (!t.vencimiento) return;
      const k = String(t.vencimiento).slice(0, 10);
      if (!map[k]) map[k] = [];
      map[k].push(t);
    });
    return map;
  }, [tareas]);

  const nombreMes = new Date(mes.y, mes.m, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

  return (
    <div data-testid="tareas-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-heading font-bold text-ink-900">Tareas</h1>
          <p className="text-ink-500 mt-1">
            To-do con responsable, vencimiento y vínculos a cliente, contacto o visita.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {Object.keys(syncMap).length > 0 && (
            <Button
              variant="outline"
              onClick={sincronizarTareasAhora}
              disabled={syncing}
              data-testid="btn-sync-tareas"
              title="Sincroniza con Microsoft To Do / Google Tasks"
            >
              {syncing ? <span className="inline-block w-4 h-4 mr-1.5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /> : '🔄 '}
              Sincronizar tareas
            </Button>
          )}
          {(user?.rol === 'admin' || user?.rol === 'dir_comercial') && (
            <Button
              variant="outline"
              onClick={async () => { try { await exportApi.descargar('tareas'); toast.success('CSV descargado'); } catch (e) { toast.error(e.message); } }}
              data-testid="btn-export-tareas"
            >
              <Download className="w-4 h-4 mr-1.5" /> Exportar CSV
            </Button>
          )}
          <Button className="bg-brand-500 hover:bg-brand-600 text-white" onClick={abrirNueva} data-testid="btn-nueva-tarea">
            <Plus className="w-4 h-4 mr-1.5" /> Nueva tarea
          </Button>
        </div>
      </div>

      {/* Switch vista + filtros */}
      <Card className="p-3 mb-4 border-ink-200 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-ink-200 bg-white p-1">
          <button
            onClick={() => setVista('lista')}
            className={`inline-flex items-center gap-1 px-3 py-1 rounded text-sm font-medium ${vista === 'lista' ? 'bg-brand-50 text-brand-700' : 'text-ink-600'}`}
            data-testid="vista-lista"
          >
            <List className="w-3.5 h-3.5" /> Lista
          </button>
          <button
            onClick={() => setVista('calendario')}
            className={`inline-flex items-center gap-1 px-3 py-1 rounded text-sm font-medium ${vista === 'calendario' ? 'bg-brand-50 text-brand-700' : 'text-ink-600'}`}
            data-testid="vista-calendario"
          >
            <CalendarIcon className="w-3.5 h-3.5" /> Calendario
          </button>
          <button
            onClick={() => setVista('eisenhower')}
            className={`inline-flex items-center gap-1 px-3 py-1 rounded text-sm font-medium ${vista === 'eisenhower' ? 'bg-violet-50 text-violet-700' : 'text-ink-600'}`}
            data-testid="vista-eisenhower"
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Eisenhower
          </button>
        </div>
        <div className="inline-flex rounded-md border border-ink-200 bg-white p-1 ml-2">
          <button onClick={() => setFiltroScope('mias')} className={`px-3 py-1 text-sm rounded ${filtroScope === 'mias' ? 'bg-brand-50 text-brand-700' : 'text-ink-600'}`} data-testid="filter-mias">Mis tareas</button>
          <button onClick={() => setFiltroScope('todas')} className={`px-3 py-1 text-sm rounded ${filtroScope === 'todas' ? 'bg-brand-50 text-brand-700' : 'text-ink-600'}`} data-testid="filter-todas">Todas</button>
        </div>
        <select className="h-8 rounded-md border border-ink-200 bg-white text-sm px-2 text-ink-700" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} data-testid="filter-estado-tareas">
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="en_curso">En curso</option>
          <option value="hecha">Hecha</option>
          <option value="cancelada">Cancelada</option>
        </select>
        <select className="h-8 rounded-md border border-ink-200 bg-white text-sm px-2 text-ink-700" value={filtroPrioridad} onChange={(e) => setFiltroPrioridad(e.target.value)} data-testid="filter-prioridad">
          <option value="">Todas las prioridades</option>
          <option value="alta">Prioridad alta</option>
          <option value="media">Prioridad media</option>
          <option value="baja">Prioridad baja</option>
        </select>
        {isAdmin && (
          <>
            <select className="h-8 rounded-md border border-ink-200 bg-white text-sm px-2 text-ink-700" value={filtroRol}
              onChange={(e) => { setFiltroRol(e.target.value); if (e.target.value) setFiltroScope('todas'); }}
              data-testid="filter-rol-responsable">
              <option value="">Rol: todos</option>
              <option value="admin">Administrador</option>
              <option value="dir_comercial">Dir. comercial</option>
              <option value="comercial">Comercial</option>
              <option value="agente_libre">Agente libre</option>
            </select>
            <select className="h-8 rounded-md border border-ink-200 bg-white text-sm px-2 text-ink-700" value={filtroResponsable}
              onChange={(e) => { setFiltroResponsable(e.target.value); setFiltroScope('todas'); }}
              data-testid="filter-responsable">
              <option value="">Responsable: todos</option>
              {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nombre} {u.apellidos || ''}</option>)}
            </select>
          </>
        )}
        <div className="inline-flex items-center gap-1 text-xs text-ink-500 ml-1">
          <span>Vence entre</span>
          <input
            type="date"
            value={filtroDesde}
            onChange={(e) => setFiltroDesde(e.target.value)}
            className="h-8 rounded-md border border-ink-200 bg-white text-sm px-2 text-ink-700"
            data-testid="filter-desde"
          />
          <span>y</span>
          <input
            type="date"
            value={filtroHasta}
            onChange={(e) => setFiltroHasta(e.target.value)}
            className="h-8 rounded-md border border-ink-200 bg-white text-sm px-2 text-ink-700"
            data-testid="filter-hasta"
          />
          {(filtroDesde || filtroHasta) && (
            <button
              onClick={() => { setFiltroDesde(''); setFiltroHasta(''); }}
              className="ml-1 text-ink-400 hover:text-ink-700"
              title="Limpiar rango"
              data-testid="filter-fechas-reset"
            >×</button>
          )}
        </div>
      </Card>

      {/* Lista */}
      {vista === 'lista' && (
        <Card className="border-ink-200 bg-white" data-testid="tareas-lista">
          <div className="divide-y divide-ink-100">
            {tareas.length === 0 && (
              <EmptyState
                icon={CheckSquare}
                title="Sin tareas pendientes"
                description="Crea la primera tarea o ajusta los filtros para ver más resultados."
                testid="tareas-empty"
              />
            )}
            {tareas.map((t) => <TareaRow key={t.id} t={t} syncProviders={syncMap[t.id] || []} onToggle={() => toggleEstado(t)} onEdit={() => abrirEditar(t)} onDelete={() => borrar(t)} onNavEntidad={() => t.entidad_id && navigate(`/clientes/${t.entidad_id}`)} navigate={navigate} asignadoActivo={!t.asignado_user_id || activeUserIds.has(t.asignado_user_id)} />)}
          </div>
        </Card>
      )}

      {/* Calendario */}
      {vista === 'calendario' && (
        <Card className="border-ink-200 bg-white p-4" data-testid="tareas-calendario">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setMes({ y: mes.m === 0 ? mes.y - 1 : mes.y, m: mes.m === 0 ? 11 : mes.m - 1 })} className="p-2 rounded hover:bg-ink-50" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
            <h3 className="font-heading font-bold text-ink-900 capitalize">{nombreMes}</h3>
            <button onClick={() => setMes({ y: mes.m === 11 ? mes.y + 1 : mes.y, m: mes.m === 11 ? 0 : mes.m + 1 })} className="p-2 rounded hover:bg-ink-50" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-[11px] uppercase text-ink-500 font-semibold tracking-wider mb-1">
            {['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'].map((d) => <div key={d} className="text-center py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((d, i) => {
              if (!d) return <div key={i} className="bg-ink-50 rounded min-h-[80px]" />;
              const k = fmtDateLocal(d);
              const dayTareas = tareasPorDia[k] || [];
              const hoy = new Date(); hoy.setHours(0,0,0,0);
              const esHoy = d.getTime() === hoy.getTime();
              return (
                <div key={i} className={`border rounded p-1 min-h-[80px] ${esHoy ? 'border-brand-400 bg-brand-50/30' : 'border-ink-200 bg-white'}`}>
                  <div className={`text-[11px] font-semibold mb-1 ${esHoy ? 'text-brand-700' : 'text-ink-600'}`}>{d.getDate()}</div>
                  <div className="space-y-0.5">
                    {dayTareas.slice(0, 3).map((t) => (
                      <button
                        key={t.id}
                        onClick={() => abrirEditar(t)}
                        className={`w-full text-left text-[10px] px-1 py-0.5 rounded truncate border ${ESTADO_STYLE[t.estado] || ''} hover:opacity-80`}
                        title={t.titulo}
                        data-testid={`cal-tarea-${t.id}`}
                      >
                        {t.titulo}
                      </button>
                    ))}
                    {dayTareas.length > 3 && <div className="text-[10px] text-ink-500 px-1">+{dayTareas.length - 3}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Matriz Eisenhower */}
      {vista === 'eisenhower' && (
        <EisenhowerMatrix
          tareas={tareas}
          onToggleEstado={toggleEstado}
          onEdit={abrirEditar}
          onToggleCuadrante={toggleCuadrante}
          onMoverCuadrante={moverCuadrante}
          onNavEntidad={(t) => t.entidad_id && navigate(`/clientes/${t.entidad_id}`)}
        />
      )}

      {/* Dialog crear/editar */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) setDialogOpen(false); }}>
        <DialogContent className="max-w-lg" data-testid="dialog-tarea">
          <form onSubmit={guardar}>
            <DialogHeader>
              <DialogTitle>{editing ? 'Editar tarea' : 'Nueva tarea'}</DialogTitle>
              <DialogDescription>
                Asigna responsable y fecha de vencimiento para que aparezca en el calendario.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-ink-600 mb-1 block">Título *</label>
                <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} autoFocus data-testid="tf-titulo" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-ink-600 mb-1 block">Descripción</label>
                <Textarea rows={2} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
              </div>
              {editing?.origen_correo_message_id && (
                <div className="sm:col-span-2 rounded-md border border-ink-200 bg-ink-50/70 p-3 space-y-1.5" data-testid="tarea-correo-origen">
                  <p className="text-xs font-semibold text-ink-800 uppercase tracking-wide">Correo origen</p>
                  {(editing.origen_correo_asunto || editing.correo_asunto) && (
                    <p className="text-sm text-ink-900">
                      <span className="text-ink-500 text-xs font-medium">Asunto · </span>
                      {editing.origen_correo_asunto || editing.correo_asunto}
                    </p>
                  )}
                  {editing.origen_correo_from && (
                    <p className="text-sm text-ink-900">
                      <span className="text-ink-500 text-xs font-medium">Remitente · </span>
                      {editing.origen_correo_from}
                    </p>
                  )}
                  {(editing.origen_correo_fecha) && (
                    <p className="text-sm text-ink-900">
                      <span className="text-ink-500 text-xs font-medium">Fecha · </span>
                      {formatFecha(editing.origen_correo_fecha) || String(editing.origen_correo_fecha)}
                    </p>
                  )}
                  <Link
                    to={`/correos?message_id=${encodeURIComponent(editing.origen_correo_message_id)}`}
                    className="inline-flex text-sm font-medium text-brand-700 hover:underline pt-0.5"
                    data-testid="tarea-link-correo-origen"
                  >
                    Abrir en bandeja
                  </Link>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block">Vencimiento</label>
                <Input type="date" value={form.vencimiento || ''} onChange={(e) => setForm({ ...form, vencimiento: e.target.value })} data-testid="tf-vencimiento" />
              </div>
              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block">Prioridad</label>
                <select className="w-full h-10 rounded-md border border-ink-200 bg-white text-sm px-3" value={form.prioridad} onChange={(e) => setForm({ ...form, prioridad: e.target.value })}>
                  <option value="baja">Baja</option>
                  <option value="media">Media</option>
                  <option value="alta">Alta</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-ink-600 mb-1 block">Responsable *</label>
                <select className="w-full h-10 rounded-md border border-ink-200 bg-white text-sm px-3" value={form.asignado_user_id} onChange={(e) => setForm({ ...form, asignado_user_id: e.target.value })} data-testid="tf-asignado">
                  <option value="">— Elegir —</option>
                  {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nombre} {u.apellidos || ''}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block">Cliente (opcional)</label>
                <select className="w-full h-10 rounded-md border border-ink-200 bg-white text-sm px-3" value={form.entidad_id} onChange={(e) => setForm({ ...form, entidad_id: e.target.value, contacto_id: '' })}>
                  <option value="">— Sin cliente —</option>
                  {entidades.map((e) => <option key={e.id} value={e.id}>{e.nombre_comercial || e.razon_social}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block">Contacto (opcional)</label>
                <select className="w-full h-10 rounded-md border border-ink-200 bg-white text-sm px-3" value={form.contacto_id} onChange={(e) => setForm({ ...form, contacto_id: e.target.value })} disabled={!form.entidad_id}>
                  <option value="">— Sin contacto —</option>
                  {contactos.filter((c) => c.entidad_id === form.entidad_id).map((c) => <option key={c.id} value={c.id}>{c.nombre} {c.apellidos || ''}</option>)}
                </select>
              </div>
              {/* Clasificación Eisenhower */}
              <div className="sm:col-span-2 flex items-center gap-4 py-1">
                <span className="text-xs font-medium text-ink-600">Matriz Eisenhower:</span>
                <label className="flex items-center gap-1.5 cursor-pointer select-none">
                  <input type="checkbox" checked={!!form.urgente} onChange={(e) => setForm({ ...form, urgente: e.target.checked })}
                    className="accent-red-500" data-testid="tf-urgente" />
                  <Zap className="w-3.5 h-3.5 text-red-500" />
                  <span className="text-sm text-ink-700">Urgente</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer select-none">
                  <input type="checkbox" checked={!!form.importante} onChange={(e) => setForm({ ...form, importante: e.target.checked })}
                    className="accent-violet-500" data-testid="tf-importante" />
                  <Star className="w-3.5 h-3.5 text-violet-500" />
                  <span className="text-sm text-ink-700">Importante</span>
                </label>
                {(form.urgente || form.importante) && (
                  <span className="text-[11px] text-ink-400">
                    → {form.urgente && form.importante ? '🟥 Hacer ahora' : form.urgente ? '🟨 Delegar' : '🟦 Planificar'}
                  </span>
                )}
              </div>
              {err && <div className="sm:col-span-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="btn-guardar-tarea">{editing ? 'Guardar' : 'Crear tarea'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={confirmDel.open}
        onOpenChange={(o) => setConfirmDel((s) => ({ ...s, open: o }))}
        resource="tarea"
        itemName={confirmDel.item?.titulo}
        onConfirm={confirmarBorrar}
        testidPrefix="confirm-del-tarea"
      />
    </div>
  );
};

const TareaRow = ({ t, syncProviders = [], onToggle, onEdit, onDelete, onNavEntidad, navigate, asignadoActivo = true }) => {
  const hoy = new Date(); hoy.setHours(0,0,0,0);
  const venc = t.vencimiento ? new Date(t.vencimiento) : null;
  const vencida = venc && venc < hoy && t.estado !== 'hecha' && t.estado !== 'cancelada';
  return (
    <div className="p-4 flex items-start gap-3 hover:bg-ink-50" data-testid={`tarea-${t.id}`}>
      <button onClick={onToggle} className="mt-0.5" title={t.estado === 'hecha' ? 'Marcar pendiente' : 'Marcar hecha'}>
        {t.estado === 'hecha'
          ? <CheckSquare className="w-5 h-5 text-emerald-600" />
          : <Square className={`w-5 h-5 ${vencida ? 'text-amber-600' : 'text-ink-400'}`} />}
      </button>
      <div className="flex-1 min-w-0">
        <p className={`font-semibold text-ink-900 ${t.estado === 'hecha' ? 'line-through text-ink-400' : ''}`}>{t.titulo}</p>
        {t.descripcion && <p className="text-xs text-ink-500 mt-0.5 line-clamp-2">{t.descripcion}</p>}
        <div className="flex flex-wrap items-center gap-2 mt-1.5">
          <Badge className={`${ESTADO_STYLE[t.estado]} border text-[10px]`}>{ESTADO_LABEL[t.estado]}</Badge>
          <Badge className={`${PRIORIDAD_STYLE[t.prioridad]} border text-[10px]`}>Prioridad {t.prioridad}</Badge>
          {syncProviders.length > 0 && (
            <Badge
              className="bg-blue-50 border-blue-200 text-blue-700 border text-[10px]"
              title={`Sincronizada con ${syncProviders.map((p) => p === 'microsoft' ? 'Microsoft To Do' : 'Google Tasks').join(' y ')}`}
              data-testid={`sync-badge-${t.id}`}
            >
              🔄 {syncProviders.length === 1 ? (syncProviders[0] === 'microsoft' ? 'To Do' : 'Tasks') : 'Sync'}
            </Badge>
          )}
          {t.vencimiento && (
            <span className={`text-[11px] inline-flex items-center gap-1 ${vencida ? 'text-amber-700 font-semibold' : 'text-ink-500'}`}>
              {vencida && <AlertTriangle className="w-3 h-3" />}
              <Clock className="w-3 h-3" /> {formatFecha(t.vencimiento)}
            </span>
          )}
          <span className="text-[11px] text-ink-500">· {t.asignado_nombre || '—'}</span>
          {t.entidad_nombre && (
            <button onClick={onNavEntidad} className="text-[11px] text-brand-700 hover:underline">
              · {t.entidad_nombre}
            </button>
          )}
          {t.contacto_nombre && <span className="text-[11px] text-ink-500">· {t.contacto_nombre}</span>}
          {(t.origen_correo_message_id || t.correo_id) && (
            <button
              onClick={() => navigate(`/correos?message_id=${encodeURIComponent(t.origen_correo_message_id || t.correo_id)}`)}
              className="text-[11px] text-brand-600 hover:underline flex items-center gap-0.5"
              title={t.origen_correo_asunto ? `Correo: "${t.origen_correo_asunto}"` : 'Ver correo origen'}
              data-testid={`tarea-correo-link-${t.id}`}
            >
              <Mail className="w-3 h-3" /> Correo origen
            </button>
          )}
        </div>
      </div>
      <div className="flex gap-1">
        {asignadoActivo
          ? <button onClick={onEdit} className="p-1.5 rounded hover:bg-ink-100 text-ink-600" title="Editar"><Pencil className="w-4 h-4" /></button>
          : <span className="p-1.5 text-[10px] text-ink-400 italic self-center">inactivo</span>
        }
        <button onClick={onDelete} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

// ─── Matriz Eisenhower ────────────────────────────────────────────────────────
const CUADRANTES = [
  {
    id: 'q1', urgente: true,  importante: true,
    label: 'Hacer ahora', sublabel: 'Urgente + Importante',
    color: 'border-red-300 bg-red-50',
    header: 'bg-red-100 text-red-800',
    badge: 'bg-red-100 text-red-700 border-red-200',
  },
  {
    id: 'q2', urgente: false, importante: true,
    label: 'Planificar', sublabel: 'Importante, no urgente',
    color: 'border-violet-300 bg-violet-50',
    header: 'bg-violet-100 text-violet-800',
    badge: 'bg-violet-100 text-violet-700 border-violet-200',
  },
  {
    id: 'q3', urgente: true,  importante: false,
    label: 'Delegar', sublabel: 'Urgente, no importante',
    color: 'border-amber-300 bg-amber-50',
    header: 'bg-amber-100 text-amber-800',
    badge: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  {
    id: 'q4', urgente: false, importante: false,
    label: 'Eliminar / Postergar', sublabel: 'No urgente, no importante',
    color: 'border-ink-200 bg-ink-50',
    header: 'bg-ink-100 text-ink-600',
    badge: 'bg-ink-100 text-ink-600 border-ink-200',
  },
];

const EisenhowerMatrix = ({ tareas, onToggleEstado, onEdit, onToggleCuadrante, onMoverCuadrante, onNavEntidad }) => {
  const hoy = new Date(); hoy.setHours(0,0,0,0);
  const dragRef = React.useRef(null); // { tarea }
  const [dragOverId, setDragOverId] = React.useState(null); // id del cuadrante bajo el cursor

  // Tareas sin clasificar: ni urgente ni importante están definidas (legacy)
  const clasificadas = tareas.filter((t) => t.urgente !== undefined && t.importante !== undefined);
  const sinClasificar = tareas.filter((t) => !t.urgente && !t.importante && t.urgente === false && t.importante === false
    ? false  // tiene ambas en false explícito → cuadrante 4, no "sin clasificar"
    : false
  );
  // En realidad todas tienen defaults del backend, solo separamos las que no han sido tocadas por el usuario
  // Usamos un heurístico: si la tarea fue creada antes del sprint Eisenhower no tendrá el campo en la BD
  // Como el backend ya devuelve defaults, todas irán a Q4 por defecto — esto es correcto.

  return (
    <div data-testid="eisenhower-matrix">
      <div className="mb-3 flex items-center gap-2">
        <LayoutGrid className="w-4 h-4 text-violet-600" />
        <h2 className="font-heading font-semibold text-ink-800">Matriz Eisenhower</h2>
        <span className="text-xs text-ink-400">· {tareas.length} tareas · Haz clic en ⚡ o ★ de cada tarea para reclasificarla</span>
      </div>

      {/* Eje Y label */}
      <div className="flex">
        <div className="flex flex-col items-center justify-center w-6 mr-2 text-[10px] text-ink-400 uppercase tracking-widest shrink-0" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
          Importancia ↑
        </div>
        <div className="flex-1">
          {/* Eje X label */}
          <div className="flex justify-between text-[10px] text-ink-400 uppercase tracking-widest mb-1 px-2">
            <span>← No urgente</span>
            <span>Urgente →</span>
          </div>

          {/* Grid 2x2 — orden: [Q2 planificar] [Q1 hacer ahora] / [Q4 postergar] [Q3 delegar] */}
          <div className="grid grid-cols-2 gap-3" style={{ minHeight: '480px' }}>
            {[CUADRANTES[1], CUADRANTES[0], CUADRANTES[3], CUADRANTES[2]].map((q) => {
              const qTareas = tareas.filter((t) =>
                !!t.urgente === q.urgente && !!t.importante === q.importante &&
                t.estado !== 'hecha' && t.estado !== 'cancelada'
              );
              const hechas = tareas.filter((t) =>
                !!t.urgente === q.urgente && !!t.importante === q.importante &&
                (t.estado === 'hecha' || t.estado === 'cancelada')
              );
              return (
                <div
                  key={q.id}
                  className={`rounded-lg border-2 flex flex-col transition-shadow ${dragOverId === q.id ? 'border-brand-400 shadow-lg shadow-brand-100 scale-[1.01]' : q.color}`}
                  data-testid={`cuadrante-${q.id}`}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverId(q.id); }}
                  onDragLeave={() => setDragOverId(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOverId(null);
                    if (dragRef.current) { onMoverCuadrante(dragRef.current, q); dragRef.current = null; }
                  }}
                >
                  <div className={`${q.header} px-3 py-2 rounded-t-md`}>
                    <div className="font-semibold text-sm">{q.label}</div>
                    <div className="text-[11px] opacity-70">{q.sublabel} · {qTareas.length} pendientes</div>
                  </div>
                  <div className="flex-1 p-2 space-y-1.5 overflow-y-auto" style={{ maxHeight: '340px' }}>
                    {qTareas.length === 0 && (
                      <p className="text-[11px] text-ink-400 text-center py-4">Sin tareas</p>
                    )}
                    {qTareas.map((t) => {
                      const venc = t.vencimiento ? new Date(t.vencimiento) : null;
                      const vencida = venc && venc < hoy;
                      return (
                        <div
                          key={t.id}
                          draggable
                          onDragStart={(e) => { dragRef.current = t; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', t.id); }}
                          onDragEnd={() => { dragRef.current = null; setDragOverId(null); }}
                          className="bg-white rounded border border-ink-100 px-2.5 py-2 flex items-start gap-2 hover:shadow-sm transition cursor-grab active:cursor-grabbing active:opacity-50"
                          data-testid={`eisen-tarea-${t.id}`}
                        >
                          <button onClick={() => onToggleEstado(t)} className="shrink-0 mt-0.5" title="Marcar hecha">
                            <Square className="w-4 h-4 text-ink-300 hover:text-emerald-500" />
                          </button>
                          <div className="flex-1 min-w-0">
                            <button onClick={() => onEdit(t)} className="text-left w-full">
                              <p className="text-sm font-medium text-ink-900 truncate hover:text-brand-700">{t.titulo}</p>
                            </button>
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              {t.entidad_nombre && (
                                <button onClick={() => onNavEntidad(t)} className="text-[10px] text-brand-600 hover:underline truncate max-w-[120px]">{t.entidad_nombre}</button>
                              )}
                              {t.vencimiento && (
                                <span className={`text-[10px] flex items-center gap-0.5 ${vencida ? 'text-red-600 font-semibold' : 'text-ink-400'}`}>
                                  {vencida && <AlertTriangle className="w-3 h-3" />}
                                  <Clock className="w-3 h-3" />{String(t.vencimiento).slice(0,10)}
                                </span>
                              )}
                            </div>
                          </div>
                          {/* Badges de reclasificación */}
                          <div className="flex gap-1 shrink-0">
                            <button
                              onClick={() => onToggleCuadrante(t, 'urgente')}
                              title={t.urgente ? 'Quitar urgente' : 'Marcar urgente'}
                              className={`w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center border transition ${t.urgente ? 'bg-red-100 text-red-700 border-red-300' : 'bg-white text-ink-300 border-ink-200 hover:border-red-300 hover:text-red-500'}`}
                              data-testid={`eisen-urgente-${t.id}`}
                            >
                              <Zap className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => onToggleCuadrante(t, 'importante')}
                              title={t.importante ? 'Quitar importante' : 'Marcar importante'}
                              className={`w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center border transition ${t.importante ? 'bg-violet-100 text-violet-700 border-violet-300' : 'bg-white text-ink-300 border-ink-200 hover:border-violet-300 hover:text-violet-500'}`}
                              data-testid={`eisen-importante-${t.id}`}
                            >
                              <Star className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {hechas.length > 0 && (
                      <p className="text-[10px] text-ink-400 text-center pt-1">{hechas.length} completada{hechas.length > 1 ? 's' : ''} oculta{hechas.length > 1 ? 's' : ''}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tareas;
